import {
  LayoutGrid,
  Calculator,
  Boxes,
  Wallet,
  MapPin,
  ClipboardCheck,
  Newspaper,
} from 'lucide-react'
import { PhoneFrame } from './PhoneFrame'

type Feature = {
  icon: React.ReactNode
  eyebrow: string
  title: string
  body: string
  screenshot: string
  alt: string
}

const features: Feature[] = [
  {
    icon: <LayoutGrid size={22} />,
    eyebrow: 'The whole toolkit',
    title: 'Every tool an agent needs, one home screen away.',
    body: 'Premium Calculators. Recent Quotes. Renewal Pipeline. Claim Services. Office Locator. Cashless Hospital. Cashless Garage. Proposal Forms. Claim Forms. Self Inspection. Insurance News. All in one place — no more juggling apps.',
    screenshot: '/screenshots/dashboard.jpeg',
    alt: 'BimaUncle all-in-one dashboard',
  },
  {
    icon: <Calculator size={22} />,
    eyebrow: 'Motor',
    title: 'From private car to e-rickshaw.',
    body: 'Pick a vehicle type, fill the form, see the premium. Motor covers Private Car, Two-Wheeler, Taxi, GCV, PCV Bus, 3-Wheeler, Rickshaw, E-Cart, E-Rickshaw and more — each tuned to the way that policy actually works.',
    screenshot: '/screenshots/motor.jpeg',
    alt: 'Motor premium calculators inside BimaUncle',
  },
  {
    icon: <Boxes size={22} />,
    eyebrow: 'Beyond Motor',
    title: 'Health, Engineering, PA, Liability and more.',
    body: 'Family Medicare. Arogya Sanjeevani. Contractor All Risk. Workmen’s Compensation. Cyber Kavach. Wedding Bells. Cattle / Livestock. Whatever you sell, the calculator is already inside.',
    screenshot: '/screenshots/health.jpeg',
    alt: 'Health insurance premium calculators inside BimaUncle',
  },
  {
    icon: <Wallet size={22} />,
    eyebrow: 'Policy Wallet',
    title: 'Scan once. Never miss a renewal.',
    body: 'Scan a policy QR or add manually. The wallet shows policies, premium under management, customers, and what’s expiring in the next 30 days. Search by name, policy number, vehicle or mobile.',
    screenshot: '/screenshots/policy-wallet.jpeg',
    alt: 'BimaUncle Policy Wallet',
  },
  {
    icon: <MapPin size={22} />,
    eyebrow: 'On the move',
    title: 'Offices and cashless hospitals, right there.',
    body: 'Type a pincode, get the nearest divisional and branch offices. Need a cashless hospital for a customer? The hospital finder lists empanelled hospitals by area so you can answer on the spot.',
    screenshot: '/screenshots/office-finder.jpeg',
    alt: 'Office finder inside BimaUncle',
  },
  {
    icon: <ClipboardCheck size={22} />,
    eyebrow: 'Claims & service',
    title: 'Surveyor finder, NCB confirmation, and more.',
    body: 'Raise a spot or final survey request. Find a surveyor by district. Draft an NCB-confirmation email to the previous insurer with policy and vehicle details pre-filled. The everyday admin work, simplified.',
    screenshot: '/screenshots/claim-services.jpeg',
    alt: 'Claim related services inside BimaUncle',
  },
  {
    icon: <Newspaper size={22} />,
    eyebrow: 'Stay informed',
    title: 'Insurance news in your language.',
    body: 'A live feed of insurance-industry news — regulatory, markets, and trending stories — available in English, Hindi, Malayalam, Telugu, Tamil, Bengali and Marathi. Skim the highlights, read the full piece when you have a minute.',
    screenshot: '/screenshots/insurance-news.jpeg',
    alt: 'Insurance news inside BimaUncle',
  },
]

export function FeatureShowcase() {
  return (
    <section id="features" className="mx-auto max-w-6xl px-5 py-16 md:py-20">
      <div className="max-w-2xl">
        <span className="chip bg-brand/10 text-brand">What it does</span>
        <h2 className="mt-3">The whole job. In one app.</h2>
        <p className="mt-3 text-ink-soft">
          A quick tour of the app — every screen here is real.
        </p>
      </div>

      <div className="mt-12 flex flex-col gap-20">
        {features.map((f, i) => (
          <div
            key={f.title}
            className={`grid items-center gap-8 md:grid-cols-12 md:gap-12 ${
              i % 2 === 1 ? 'md:[&>:first-child]:order-2' : ''
            }`}
          >
            <div className="md:col-span-6">
              <PhoneFrame src={f.screenshot} alt={f.alt} size="md" glow />
            </div>
            <div className="md:col-span-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                {f.icon}
              </div>
              <span className="mt-3 inline-block text-xs font-bold uppercase tracking-wider text-ink-soft">
                {f.eyebrow}
              </span>
              <h3 className="mt-1 text-2xl md:text-3xl">{f.title}</h3>
              <p className="mt-3 text-ink-soft">{f.body}</p>
            </div>
          </div>
        ))}
      </div>

      <ScreenshotStrip />
    </section>
  )
}

function ScreenshotStrip() {
  const shots = [
    { src: '/screenshots/calculators-overview.jpeg', label: 'All calculators' },
    { src: '/screenshots/engineering.jpeg', label: 'Engineering' },
    { src: '/screenshots/personal-accident.jpeg', label: 'Personal Accident' },
    { src: '/screenshots/liability.jpeg', label: 'Liability' },
    { src: '/screenshots/package-policies.jpeg', label: 'Package Policies' },
    { src: '/screenshots/misc-products.jpeg', label: 'Miscellaneous' },
    { src: '/screenshots/social-rural.jpeg', label: 'Social & Rural' },
    { src: '/screenshots/fire-quote-detail.jpeg', label: 'Fire — quote builder' },
    { src: '/screenshots/hospital-finder.jpeg', label: 'Cashless Hospitals' },
  ]
  return (
    <div className="mt-20">
      <div className="max-w-2xl">
        <span className="chip bg-brand/10 text-brand">More screens</span>
        <h3 className="mt-3 text-2xl">A quick look around.</h3>
        <p className="mt-2 text-sm text-ink-soft">
          Scroll across — every screen is from the app.
        </p>
      </div>
      <div
        className="mt-6 -mx-5 overflow-x-auto pb-4 sm:mx-0"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="flex w-max gap-5 px-5">
          {shots.map((s) => (
            <figure key={s.src} className="w-[180px] shrink-0 sm:w-[210px]">
              <PhoneFrame src={s.src} alt={s.label} size="sm" />
              <figcaption className="mt-3 text-center text-xs font-medium text-ink-soft">
                {s.label}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  )
}
